import { api } from './client';

/** Os tipos que cada membro compartilha (docs/PLANO_COMPARTILHAMENTO_ESCRITORIO.md A5). */
export type TipoCompartilhavel = 'PROCESSO' | 'CLIENTE' | 'ARTEFATO' | 'MODELO_PETICAO';
export type ModoPolitica = 'TODOS' | 'SELECIONADOS' | 'NENHUM';
export type TipoAlvo = 'ESCRITORIO' | 'PAPEL' | 'EQUIPE' | 'USUARIO';

export interface AlvoCompartilhamento {
  tipo: TipoAlvo;
  /** O papel, o id da equipe ou o id da pessoa; ausente em ESCRITORIO. */
  valor?: string | null;
}

export interface PoliticaCompartilhamento {
  recurso_tipo: TipoCompartilhavel;
  modo: ModoPolitica;
  incluir_proximos: boolean;
  alvos: AlvoCompartilhamento[];
  /** O membro segue o padrão do escritório neste tipo (sem política própria). */
  segue_padrao?: boolean;
}

export interface CompartilhamentoDoMembro {
  politicas: PoliticaCompartilhamento[];
  /** O padrão do escritório por tipo, que o Proprietário define. */
  padrao?: PoliticaCompartilhamento[];
  /** Depois de "Seguir o padrão": quantos itens ficaram para os colegas só lerem. */
  retidos_agora?: number | null;
  meu_role: string;
  /** Recebe por e-mail as intimações dos processos de colegas de que cuida (Bloco 5). */
  recebe_avisos?: boolean;
}

export async function fetchMeuCompartilhamento(escritorioId: string): Promise<CompartilhamentoDoMembro> {
  const { data } = await api.get<CompartilhamentoDoMembro>(`/escritorios/${escritorioId}/compartilhamento`);
  return data;
}

export async function definirPolitica(
  escritorioId: string,
  tipo: TipoCompartilhavel,
  politica: Omit<PoliticaCompartilhamento, 'recurso_tipo' | 'segue_padrao'>,
): Promise<PoliticaCompartilhamento & { compartilhados: number; retidos_agora: number }> {
  const { data } = await api.put(`/escritorios/${escritorioId}/compartilhamento/${tipo}`, politica);
  return data;
}

/** O Proprietário muda o padrão do escritório; vale para quem o segue. */
export async function definirPadrao(
  escritorioId: string,
  tipo: TipoCompartilhavel,
  politica: Omit<PoliticaCompartilhamento, 'recurso_tipo' | 'segue_padrao'>,
): Promise<PoliticaCompartilhamento & { compartilhados: number; retidos_agora: number; seguidores: number }> {
  const { data } = await api.put(`/escritorios/${escritorioId}/compartilhamento/padrao/${tipo}`, politica);
  return data;
}

/** O membro volta a seguir o padrão do escritório no tipo. */
export async function seguirPadrao(escritorioId: string, tipo: TipoCompartilhavel): Promise<CompartilhamentoDoMembro> {
  const { data } = await api.delete<CompartilhamentoDoMembro>(`/escritorios/${escritorioId}/compartilhamento/${tipo}`);
  return data;
}

/** Um membro que o gestor pode "visualizar como" (A8, Bloco 6). */
export interface Supervisionado {
  usuario: { id: string; nome: string };
  escritorio_id: string;
  escritorio_nome: string;
}

/** Quem o usuário pode "visualizar como": vazio para quem não é gestor. Rota da pessoa (sem atuação). */
export async function fetchSupervisionados(): Promise<Supervisionado[]> {
  const { data } = await api.get<Supervisionado[]>('/escritorios/supervisionados');
  return data;
}

/** Registra na auditoria a entrada do gestor na conta do membro. */
export async function registrarVisualizacao(usuarioId: string): Promise<void> {
  await api.post(`/escritorios/visualizar-como/${usuarioId}`);
}

/** Receber ou silenciar o resumo das intimações dos processos de que cuida (Bloco 5). */
export async function definirAvisos(escritorioId: string, recebe: boolean): Promise<CompartilhamentoDoMembro> {
  const { data } = await api.put<CompartilhamentoDoMembro>(`/escritorios/${escritorioId}/compartilhamento/avisos`, {
    recebe_avisos: recebe,
  });
  return data;
}

// ---------------------------------------------------------------- item a item e pedidos (Bloco 4c)

/** NAO: ninguém vê; POLITICA: pela regra ou pelo lote; ITEM: um a um; RETIDO: os colegas só leem. */
export type EstadoDoItem = 'NAO' | 'POLITICA' | 'ITEM' | 'RETIDO';
export type StatusPedido = 'PENDENTE' | 'APROVADO' | 'RECUSADO' | 'CANCELADO';

export interface ItemNoEscritorio {
  escritorio_id: string;
  escritorio_nome: string;
  meu_role: string;
  estado: EstadoDoItem;
  alvos: AlvoCompartilhamento[];
  modo_politica: ModoPolitica;
  incluir_proximos: boolean;
  /** O padrão ao compartilhar: copiado para o item, nunca uma referência à política. */
  alvos_da_politica: AlvoCompartilhamento[];
  pedido: { id: string; status: StatusPedido; created_at: string } | null;
}

export interface PedidoRemocao {
  id: string;
  recurso_tipo: TipoCompartilhavel;
  recurso_id: string;
  rotulo: string | null;
  solicitante_id: string;
  solicitante_nome: string | null;
  status: StatusPedido;
  created_at: string;
  decidido_por_nome: string | null;
  decidido_em: string | null;
  meu: boolean;
  /** EXCLUIR: o dono pede que o processo do escritório seja excluído (Bloco 6). Ausente: RETIRAR. */
  acao?: 'RETIRAR' | 'EXCLUIR';
}

export async function fetchItemNosEscritorios(tipo: TipoCompartilhavel, id: string): Promise<ItemNoEscritorio[]> {
  const { data } = await api.get<ItemNoEscritorio[]>(`/compartilhamento-escritorio/${tipo}/${id}`);
  return data;
}

export async function compartilharItem(
  escritorioId: string,
  tipo: TipoCompartilhavel,
  id: string,
  alvos: AlvoCompartilhamento[],
): Promise<ItemNoEscritorio> {
  const { data } = await api.put<ItemNoEscritorio>(`/escritorios/${escritorioId}/itens/${tipo}/${id}`, { alvos });
  return data;
}

export async function deixarDeCompartilhar(
  escritorioId: string,
  tipo: TipoCompartilhavel,
  id: string,
): Promise<ItemNoEscritorio> {
  const { data } = await api.delete<ItemNoEscritorio>(`/escritorios/${escritorioId}/itens/${tipo}/${id}`);
  return data;
}

export async function fetchPedidosRemocao(escritorioId: string): Promise<PedidoRemocao[]> {
  const { data } = await api.get<PedidoRemocao[]>(`/escritorios/${escritorioId}/pedidos-remocao`);
  return data;
}

export async function decidirPedido(
  escritorioId: string,
  pedidoId: string,
  acao: 'aprovar' | 'recusar' | 'cancelar',
): Promise<PedidoRemocao> {
  const { data } = await api.post<PedidoRemocao>(`/escritorios/${escritorioId}/pedidos-remocao/${pedidoId}/${acao}`);
  return data;
}
